import { useState, useRef, useEffect } from 'react'
import { HexColorPicker } from 'react-colorful'
import { useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'
import { Loader2 } from 'lucide-react'

interface DeckColorPickerProps {
  deckId: Id<'flashcardDecks'>
  currentColor?: string
  defaultColor: string
  onColorChange?: (color: string) => void
}

export function DeckColorPicker({
  deckId,
  currentColor,
  defaultColor,
  onColorChange,
}: DeckColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [color, setColor] = useState(currentColor || defaultColor)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pickerRef = useRef<HTMLDivElement>(null)

  const updateDeckColor = useMutation(api.flashcards.updateDeckColor)

  // Update color when currentColor prop changes
  useEffect(() => {
    setColor(currentColor || defaultColor)
  }, [currentColor, defaultColor])

  // Close picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleColorChange = async (newColor: string) => {
    setColor(newColor)
    setIsLoading(true)
    setError(null)

    try {
      await updateDeckColor({ deckId, color: newColor })
      onColorChange?.(newColor)
    } catch (err) {
      console.error('Failed to update deck color:', err)
      setError('Failed to update color. Please try again.')
      // Revert to previous color on error
      setColor(currentColor || defaultColor)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetToDefault = async () => {
    setIsLoading(true)
    setError(null)

    try {
      setColor(defaultColor)
      await updateDeckColor({ deckId, color: defaultColor })
      onColorChange?.(defaultColor)
      setIsOpen(false)
    } catch (err) {
      console.error('Failed to reset deck color:', err)
      setError('Failed to reset color. Please try again.')
      // Revert to previous color on error
      setColor(currentColor || defaultColor)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative" ref={pickerRef}>
      {/* Color Picker Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border-2 border-sky-blue text-sky-blue font-mono text-sm hover:bg-sky-blue/10 transition-all"
        aria-label="Change deck color"
      >
        <div
          className="w-5 h-5 border-2 border-current"
          style={{ backgroundColor: color }}
        />
        <span>COLOR</span>
      </button>

      {/* Color Picker Popover */}
      {isOpen && (
        <div className="absolute z-50 mt-2 p-4 bg-black border-2 border-sky-blue shadow-lg">
          {/* Error Message */}
          {error && (
            <div className="mb-3 p-2 border-2 border-red-500 bg-red-500/10">
              <p className="text-red-500 font-mono text-xs">{error}</p>
            </div>
          )}

          <div className="mb-4">
            <div className="text-sky-blue font-mono text-sm mb-2 flex items-center gap-2">
              <span>[SELECT COLOR]</span>
              {isLoading && <Loader2 className="w-4 h-4 animate-spin text-terminal-green" />}
            </div>
            <HexColorPicker color={color} onChange={handleColorChange} />
          </div>

          {/* Color Preview */}
          <div className="mb-4 p-3 border-2 border-trail-tan">
            <div className="text-trail-tan font-mono text-xs mb-1">PREVIEW:</div>
            <div
              className="h-8 border-2"
              style={{ backgroundColor: color, borderColor: color }}
            />
            <div className="text-trail-tan font-mono text-xs mt-1 text-center">
              {color.toUpperCase()}
            </div>
          </div>

          {/* Reset Button */}
          <button
            onClick={handleResetToDefault}
            disabled={isLoading}
            className="w-full px-3 py-2 border-2 border-terminal-green text-terminal-green font-mono text-sm hover:bg-terminal-green hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>RESETTING...</span>
              </>
            ) : (
              <span>RESET TO DEFAULT</span>
            )}
          </button>
        </div>
      )}
    </div>
  )
}
